import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { findStudentByName } from '../utils/studentMatcher';
import { addAssessmentCompanyCoopMulti, addAssessmentReportCoopMulti } from '../api/crud';
import { updateSnackbar } from '../redux/action/dataAction';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  FormHelperText,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab
} from '@mui/material';

const BulkInputModalCoop = ({ 
  open, 
  onClose, 
  tableData = [],
  onRefresh = null
}) => {
  const [activeTab, setActiveTab] = useState(0); // 0 = supervisor, 1 = report
  const [bulkInputData, setBulkInputData] = useState('');
  const [previewData, setPreviewData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [jsonPreview, setJsonPreview] = useState('');
  const dispatch = useDispatch();

  // Normalize Google Sheets data to handle line breaks within cells
  const normalizeGoogleSheetsData = (text) => {
    if (!text || text.trim() === '') return '';
    const lines = text.split('\n');
    const processedLines = [];
    let currentLine = '';
    let quoteBalance = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      let quoteCount = 0;
      for (let char of line) {
        if (char === '"') quoteCount++;
      }
      quoteBalance += quoteCount;
      const insideQuotes = (quoteBalance % 2 === 1);

      const currentTabCount = (line.match(/\t/g) || []).length;
      const hasTimestampPattern = line.match(/^\d{1,2}\/\d{1,2}\/\d{4}/);
      const isNewRow = i === 0 ||
        (!insideQuotes && (
          (hasTimestampPattern && currentTabCount >= 10) ||
          currentTabCount >= 35 ||
          (currentTabCount >= 30 && line.trim().length > 50)
        ));

      if (isNewRow) {
        if (currentLine.trim()) {
          processedLines.push(currentLine.trim());
        }
        currentLine = line;
        quoteBalance = quoteCount;
      } else {
        if (currentLine) {
          currentLine += '\n' + line;
        } else {
          currentLine = line;
        }
      }
    }
    if (currentLine.trim()) {
      processedLines.push(currentLine.trim());
    }
    return processedLines.join('\n');
  };

  // Helper function to clean cell data
  const cleanCellData = (cellData) => {
    if (!cellData) return '';
    let cleaned = cellData.toString().trim();
    
    if (cleaned.startsWith('"') && cleaned.endsWith('"') && cleaned.length >= 2) {
      const middle = cleaned.slice(1, -1);
      if (!middle.includes('"') || middle.replace(/""/g, '').indexOf('"') === -1) {
        cleaned = middle;
      }
    }
    
    return cleaned
      .replace(/""/g, '"')
      .replace(/\s+/g, ' ')
      .replace(/\n/g, ' ')
      .replace(/\r/g, '')
      .replace(/\t/g, ' ')
      .trim();
  };

  // Parse bulk input data for Report assessment (14-question structure)
  const parseReportData = (inputText) => {
    const normalizedText = normalizeGoogleSheetsData(inputText);
    const lines = normalizedText.trim().split('\n');
    const parsedData = [];
    
    console.log(`Processing ${lines.length} lines for Report Assessment`);
    
    lines.forEach((line, index) => {
      const columns = line.trim().split('\t');
      
      // Report format: 10 metadata columns + 14 score columns + 1 comment = minimum 25 columns
      // Columns: ประทับเวลา, อีเมล, ผู้ประเมิน, ตำแหน่ง, เบอร์โทร, ชื่อนิสิต, หลักสูตร, สถานประกอบการ, ชื่อโครงงาน, [14 คะแนน], ข้อคิดเห็น
      if (columns.length >= 24 && columns[5]?.trim()) {
        const temp_req_id = `temp-report-${index + 1}`;
        const student_name = columns[5]?.trim() || '';
        
        let found_req_id = null;
        let found_student = null;
        
        if (student_name && tableData && tableData.length > 0) {
          found_student = findStudentByName(student_name, tableData);
          if (found_student) {
            found_req_id = found_student.req_id;
          }
        }

        // Calculate total score from 14 questions
        let totalScore = 0;
        for (let i = 9; i <= 22; i++) { // Columns 10-23 (index 9-22) = 14 questions
          const score = parseFloat(columns[i]) || 0;
          totalScore += score;
        }

        parsedData.push({
          temp_id: temp_req_id,
          found_req_id: found_req_id,
          found_student: found_student,
          lineNumber: index + 1,
          
          // Metadata (columns 0-9)
          submitted_at: cleanCellData(columns[0]),
          evaluator_email: cleanCellData(columns[1]),
          evaluator_name: cleanCellData(columns[2]),
          evaluator_position: cleanCellData(columns[3] || ''),
          evaluator_phone: cleanCellData(columns[4] || ''),
          student_name: cleanCellData(columns[5]),
          student_program: cleanCellData(columns[6] || ''),
          organization_dept: cleanCellData(columns[7] || ''),
          project_title: cleanCellData(columns[8] || ''),
          
          // 14 Assessment Questions (columns 9-22)
          q1_acknowledgement: parseFloat(columns[9]) || 0,      // กิตติกรรมประกาศ (5)
          q2_abstract: parseFloat(columns[10]) || 0,            // บทคัดย่อ (5)
          q3_toc_fig_table: parseFloat(columns[11]) || 0,       // สารบัญ สารบัญรูป ตาราง (5)
          q4_objectives_ch2: parseFloat(columns[12]) || 0,      // บทที่ 2 วัตถุประสงค์ (5)
          q5_method_ch3: parseFloat(columns[13]) || 0,          // บทที่ 3 วิธีดำเนินงาน (5)
          q6_results_ch4: parseFloat(columns[14]) || 0,         // บทที่ 4 ผลการดำเนินงาน (20)
          q7_analysis: parseFloat(columns[15]) || 0,            // การวิเคราะห์ผล (10)
          q8_conclusion_ch5: parseFloat(columns[16]) || 0,      // บทที่ 5 สรุป (10)
          q9_recommendation_ch5: parseFloat(columns[17]) || 0,  // บทที่ 5 ข้อเสนอแนะ (5)
          q10_writing_style: parseFloat(columns[18]) || 0,      // การเขียน รูปแบบ (10)
          q11_spelling_accuracy: parseFloat(columns[19]) || 0,  // การสะกดคำ ความถูกต้อง (5)
          q12_format_aesthetics: parseFloat(columns[20]) || 0,  // รูปเล่ม ความสวยงาม (5)
          q13_references: parseFloat(columns[21]) || 0,         // เอกสารอ้างอิง (5)
          q14_appendix: parseFloat(columns[22]) || 0,           // ภาคผนวก (5)
          
          total_score: totalScore,
          max_score: 100, // Total possible = 100 points
          
          // Comments (column 23+)
          comments: cleanCellData(columns[23] || ''),
          
          totalColumns: columns.length
        });
      } else {
        console.log(`Skipped report line ${index + 1}: ${columns.length} columns (need at least 24)`);
      }
    });
    
    console.log(`Parsed ${parsedData.length} valid report rows from ${lines.length} total lines`);
    return parsedData;
  };

  // Parse bulk input data for Supervisor assessment (full 20-question structure)
  const parseSupervisorData = (inputText) => {
    const normalizedText = normalizeGoogleSheetsData(inputText);
    const lines = normalizedText.trim().split('\n');
    const parsedData = [];
    
    console.log(`Processing ${lines.length} lines for Supervisor Assessment`);
    
    lines.forEach((line, index) => {
      const columns = line.trim().split('\t');
      
      // ต้องมีอย่างน้อย 28 คอลัมน์สำหรับข้อมูลสหกิจศึกษา (8 ข้อมูลพื้นฐาน + 20 คะแนน)
      if (columns.length >= 28 && (columns[5]?.trim() || columns[0]?.trim())) { 
        const temp_req_id = `temp-${index + 1}`;
        const student_name = columns[5]?.trim() || '';
        
        // ค้นหา req_id จากชื่อนิสิต
        let found_req_id = null;
        let found_student = null;
        
        if (student_name && tableData && tableData.length > 0) {
          found_student = findStudentByName(student_name, tableData);
          if (found_student) {
            found_req_id = found_student.req_id;
          }
        }

        // คำนวณคะแนนรวม (columns 8-27 = 20 คะแนน, คะแนนเต็มแต่ละข้อ 10 = รวม 200)
        let totalScore = 0;
        for (let i = 8; i <= 27; i++) {
          const score = parseFloat(columns[i]) || 0;
          totalScore += score;
        }

        parsedData.push({
          temp_id: temp_req_id,
          found_req_id: found_req_id,
          found_student: found_student,
          lineNumber: index + 1,
          // ข้อมูลพื้นฐาน (columns 0-7)
          submitted_at: cleanCellData(columns[0]), // ประทับเวลา
          evaluator_email: cleanCellData(columns[1]), // ที่อยู่อีเมล
          evaluator_name: cleanCellData(columns[2]), // ชื่อ-สกุล ผู้ประเมิน
          evaluator_position: cleanCellData(columns[3]), // ตำแหน่ง
          evaluator_phone: cleanCellData(columns[4]), // เบอร์โทรติดต่อ
          student_name: cleanCellData(columns[5]), // ชื่อ-นามสกุล นิสิตสหกิจศึกษา
          student_program: cleanCellData(columns[6]), // หลักสูตร
          organization_dept: cleanCellData(columns[7]), // สถานประกอบการ/แผนก
          
          // คะแนนการประเมิน 20 หัวข้อ (columns 8-27)
          q1_work_quantity: parseFloat(columns[8]) || 0, // ปริมาณงาน
          q2_work_quality: parseFloat(columns[9]) || 0, // คุณภาพงาน
          q3_academic_knowledge: parseFloat(columns[10]) || 0, // ความรู้ทางวิชาการ
          q4_learning_ability: parseFloat(columns[11]) || 0, // ความสามารถเรียนรู้และประยุกต์
          q5_practical_skill: parseFloat(columns[12]) || 0, // ความชำนาญด้านปฏิบัติการ
          q6_decision_analysis: parseFloat(columns[13]) || 0, // วิจารณญาณ การวิเคราะห์
          q7_planning: parseFloat(columns[14]) || 0, // การจัดการและวางแผน
          q8_communication: parseFloat(columns[15]) || 0, // ทักษะการสื่อสารและนำเสนอ
          q9_language_culture: parseFloat(columns[16]) || 0, // ภาษาและวัฒนธรรมต่างประเทศ
          q10_job_fit: parseFloat(columns[17]) || 0, // ความเหมาะสมต่อตำแหน่งงาน
          q11_responsibility: parseFloat(columns[18]) || 0, // ความรับผิดชอบและไว้วางใจได้
          q12_diligence: parseFloat(columns[19]) || 0, // ความสนใจ อุตสาหะ
          q13_initiative: parseFloat(columns[20]) || 0, // ความคิดริเริ่มและเสนอความคิดเห็น
          q14_response_to_command: parseFloat(columns[21]) || 0, // การตอบสนองต่อการสั่งการ
          q15_personality: parseFloat(columns[22]) || 0, // บุคลิกภาพ การวางตัว
          q16_relationship: parseFloat(columns[23]) || 0, // มนุษยสัมพันธ์
          q17_confidence: parseFloat(columns[24]) || 0, // ความมั่นใจในตนเอง
          q18_leadership: parseFloat(columns[25]) || 0, // ความเป็นผู้นำ
          q19_discipline: parseFloat(columns[26]) || 0, // ระเบียบวินัย ปฏิบัติตามวัฒนธรรม
          q20_ethics: parseFloat(columns[27]) || 0, // คุณธรรมและจริยธรรม
          
          total_score: totalScore, // คะแนนรวม
          
          // ข้อมูลเพิ่มเติม (columns 28-31)
          comment_strength: cleanCellData(columns[28] || ''), // ลักษณะเด่นของนิสิต
          comment_improve: cleanCellData(columns[29] || ''), // ข้อควรปรับปรุง
          future_interest: cleanCellData(columns[30] || ''), // สนใจรับเข้าทำงานในอนาคต
          overall_score: cleanCellData(columns[31] || ''), // คุณภาพโดยรวม
          
          // คำถามเพิ่มเติม 25.x (columns 32-44)
          q25_1_1: parseInt(columns[32]) || 0, // ประโยชน์จากผลงาน
          q25_1_2: parseInt(columns[33]) || 0, // โอกาสคัดเลือกพนักงาน
          q25_1_3: parseInt(columns[34]) || 0, // โอกาสความร่วมมือทางวิชาการ
          q25_2_1: parseInt(columns[35]) || 0, // สหกิจเป็นหลักสูตรบังคับ
          q25_2_2: parseInt(columns[36]) || 0, // ปฏิบัติงานอย่างน้อย 4 เดือน
          q25_3_1: parseInt(columns[37]) || 0, // หน่วยงานกลางประสานงาน
          q25_3_2: parseInt(columns[38]) || 0, // จัดส่งแบบสำรวจ
          q25_3_3: parseInt(columns[39]) || 0, // ช่องทางคัดเลือก
          q25_3_4: parseInt(columns[40]) || 0, // ระยะเวลาส่งจดหมาย
          q25_3_5: parseInt(columns[41]) || 0, // นัดหมายการนิเทศ
          q25_3_6: parseInt(columns[42]) || 0, // ประโยชน์การนิเทศ
          q25_3_7: parseInt(columns[43]) || 0, // จำนวนครั้งการนิเทศ
          q25_3_8: parseInt(columns[44]) || 0, // คุณภาพการนิเทศ
          
          // คำถาม 26 และอื่นๆ (columns 45-47)
          q26_1_usefulness: cleanCellData(columns[45] || ''), // นำผลงานไปใช้หรือไม่
          q26_2_success_level: cleanCellData(columns[46] || ''), // ระดับความสำเร็จ
          q26_reason_no_benefit: cleanCellData(columns[47] || ''), // เหตุผลไม่มีประโยชน์
          comment_other: cleanCellData(columns[48] || ''), // ข้อคิดเห็นอื่นๆ
          
          totalColumns: columns.length
        });
      } else {
        console.log(`Skipped line ${index + 1}: ${columns.length} columns`);
      }
    });
    
    console.log(`Parsed ${parsedData.length} valid supervisor rows from ${lines.length} total lines`);
    return parsedData;
  };

  // Update preview when input changes or tab switches
  useEffect(() => {
    if (bulkInputData.trim()) {
      const parsed = activeTab === 0 
        ? parseSupervisorData(bulkInputData) 
        : parseReportData(bulkInputData);
      setPreviewData(parsed);
    } else {
      setPreviewData([]);
    }
  }, [bulkInputData, tableData, activeTab]);

  // Generate JSON preview
  const handleShowJson = () => {
    const jsonData = previewData.map(item => {
      const final_req_id = item.found_req_id;
      
      if (activeTab === 1) {
        // Report data structure (14 questions)
        return {
          req_id: final_req_id || 'ไม่พบ',
          submitted_at: item.submitted_at,
          evaluator_email: item.evaluator_email,
          evaluator_name: item.evaluator_name,
          evaluator_position: item.evaluator_position,
          evaluator_phone: item.evaluator_phone,
          student_name: item.student_name,
          student_program: item.student_program,
          organization_dept: item.organization_dept,
          project_title: item.project_title,
          
          q1_acknowledgement: item.q1_acknowledgement,
          q2_abstract: item.q2_abstract,
          q3_toc_fig_table: item.q3_toc_fig_table,
          q4_objectives_ch2: item.q4_objectives_ch2,
          q5_method_ch3: item.q5_method_ch3,
          q6_results_ch4: item.q6_results_ch4,
          q7_analysis: item.q7_analysis,
          q8_conclusion_ch5: item.q8_conclusion_ch5,
          q9_recommendation_ch5: item.q9_recommendation_ch5,
          q10_writing_style: item.q10_writing_style,
          q11_spelling_accuracy: item.q11_spelling_accuracy,
          q12_format_aesthetics: item.q12_format_aesthetics,
          q13_references: item.q13_references,
          q14_appendix: item.q14_appendix,
          
          comments: item.comments,
          
          _status: final_req_id ? 'พร้อมส่ง' : 'ไม่พบข้อมูล',
          _total_score: item.total_score
        };
      }
      
      // Supervisor data structure (original full structure)
      return {
        req_id: final_req_id || 'ไม่พบ',
        submitted_at: item.submitted_at,
        evaluator_email: item.evaluator_email,
        evaluator_name: item.evaluator_name,
        evaluator_position: item.evaluator_position,
        evaluator_phone: item.evaluator_phone,
        student_name: item.student_name,
        student_program: item.student_program,
        organization_dept: item.organization_dept,
        
        q1_work_quantity: item.q1_work_quantity,
        q2_work_quality: item.q2_work_quality,
        q3_academic_knowledge: item.q3_academic_knowledge,
        q4_learning_ability: item.q4_learning_ability,
        q5_practical_skill: item.q5_practical_skill,
        q6_decision_analysis: item.q6_decision_analysis,
        q7_planning: item.q7_planning,
        q8_communication: item.q8_communication,
        q9_language_culture: item.q9_language_culture,
        q10_job_fit: item.q10_job_fit,
        q11_responsibility: item.q11_responsibility,
        q12_diligence: item.q12_diligence,
        q13_initiative: item.q13_initiative,
        q14_response_to_command: item.q14_response_to_command,
        q15_personality: item.q15_personality,
        q16_relationship: item.q16_relationship,
        q17_confidence: item.q17_confidence,
        q18_leadership: item.q18_leadership,
        q19_discipline: item.q19_discipline,
        q20_ethics: item.q20_ethics,
        
        comment_strength: item.comment_strength,
        comment_improve: item.comment_improve,
        future_interest: item.future_interest,
        overall_score: item.overall_score,
        
        q25_1_1: item.q25_1_1,
        q25_1_2: item.q25_1_2,
        q25_1_3: item.q25_1_3,
        q25_2_1: item.q25_2_1,
        q25_2_2: item.q25_2_2,
        q25_3_1: item.q25_3_1,
        q25_3_2: item.q25_3_2,
        q25_3_3: item.q25_3_3,
        q25_3_4: item.q25_3_4,
        q25_3_5: item.q25_3_5,
        q25_3_6: item.q25_3_6,
        q25_3_7: item.q25_3_7,
        q25_3_8: item.q25_3_8,
        
        q26_1_usefulness: item.q26_1_usefulness,
        q26_2_success_level: item.q26_2_success_level,
        q26_reason_no_benefit: item.q26_reason_no_benefit,
        comment_other: item.comment_other,
        
        _status: final_req_id ? 'พร้อมส่ง' : 'ไม่พบข้อมูล',
        _total_score: item.total_score
      };
    });

    setJsonPreview(JSON.stringify(jsonData, null, 2));
    setShowJsonModal(true);
  };

  // Process bulk input
  const handleBulkSubmit = async () => {
    setIsProcessing(true);
    
    try {
      const parsedData = activeTab === 0 
        ? parseSupervisorData(bulkInputData) 
        : parseReportData(bulkInputData);
      
      if (parsedData.length === 0) {
        dispatch(updateSnackbar({
          open: true,
          severity: "error",
          content: "ไม่พบข้อมูลที่ถูกต้อง กรุณาตรวจสอบรูปแบบข้อมูล"
        }));
        setIsProcessing(false);
        return;
      }

      dispatch(updateSnackbar({
        open: true,
        severity: "info",
        content: `กำลังประมวลผลข้อมูล ${parsedData.length} รายการ...`
      }));

      // กรองเฉพาะข้อมูลที่พบ req_id และเตรียมข้อมูลสำหรับ API
      const validData = [];
      const errors = [];

      parsedData.forEach(item => {
        const final_req_id = item.found_req_id;

        if (!final_req_id) {
          errors.push(`บรรทัดที่ ${item.lineNumber}: ไม่พบนักศึกษา "${item.student_name}"`);
          return;
        }

        if (activeTab === 1) {
          // Report data format (14 questions detailed structure)
          validData.push({
            req_id: final_req_id,
            submitted_at: item.submitted_at,
            evaluator_email: item.evaluator_email,
            evaluator_name: item.evaluator_name,
            evaluator_position: item.evaluator_position,
            evaluator_phone: item.evaluator_phone,
            student_name: item.student_name,
            student_program: item.student_program,
            organization_dept: item.organization_dept,
            project_title: item.project_title,
            
            q1_acknowledgement: item.q1_acknowledgement,
            q2_abstract: item.q2_abstract,
            q3_toc_fig_table: item.q3_toc_fig_table,
            q4_objectives_ch2: item.q4_objectives_ch2,
            q5_method_ch3: item.q5_method_ch3,
            q6_results_ch4: item.q6_results_ch4,
            q7_analysis: item.q7_analysis,
            q8_conclusion_ch5: item.q8_conclusion_ch5,
            q9_recommendation_ch5: item.q9_recommendation_ch5,
            q10_writing_style: item.q10_writing_style,
            q11_spelling_accuracy: item.q11_spelling_accuracy,
            q12_format_aesthetics: item.q12_format_aesthetics,
            q13_references: item.q13_references,
            q14_appendix: item.q14_appendix,
            
            comments: item.comments,
            total_score: item.total_score
          });
        } else {
          // Supervisor data format (original full structure)
          validData.push({
            req_id: final_req_id,
            submitted_at: item.submitted_at,
            evaluator_email: item.evaluator_email,
            evaluator_name: item.evaluator_name,
            evaluator_position: item.evaluator_position,
            evaluator_phone: item.evaluator_phone,
            student_name: item.student_name,
            student_program: item.student_program,
            organization_dept: item.organization_dept,
            
            q1_work_quantity: item.q1_work_quantity,
            q2_work_quality: item.q2_work_quality,
            q3_academic_knowledge: item.q3_academic_knowledge,
            q4_learning_ability: item.q4_learning_ability,
            q5_practical_skill: item.q5_practical_skill,
            q6_decision_analysis: item.q6_decision_analysis,
            q7_planning: item.q7_planning,
            q8_communication: item.q8_communication,
            q9_language_culture: item.q9_language_culture,
            q10_job_fit: item.q10_job_fit,
            q11_responsibility: item.q11_responsibility,
            q12_diligence: item.q12_diligence,
            q13_initiative: item.q13_initiative,
            q14_response_to_command: item.q14_response_to_command,
            q15_personality: item.q15_personality,
            q16_relationship: item.q16_relationship,
            q17_confidence: item.q17_confidence,
            q18_leadership: item.q18_leadership,
            q19_discipline: item.q19_discipline,
            q20_ethics: item.q20_ethics,
            
            comment_strength: item.comment_strength,
            comment_improve: item.comment_improve,
            future_interest: item.future_interest,
            overall_score: item.overall_score,
            
            q25_1_1: item.q25_1_1,
            q25_1_2: item.q25_1_2,
            q25_1_3: item.q25_1_3,
            q25_2_1: item.q25_2_1,
            q25_2_2: item.q25_2_2,
            q25_3_1: item.q25_3_1,
            q25_3_2: item.q25_3_2,
            q25_3_3: item.q25_3_3,
            q25_3_4: item.q25_3_4,
            q25_3_5: item.q25_3_5,
            q25_3_6: item.q25_3_6,
            q25_3_7: item.q25_3_7,
            q25_3_8: item.q25_3_8,
            
            q26_1_usefulness: item.q26_1_usefulness,
            q26_2_success_level: item.q26_2_success_level,
            q26_reason_no_benefit: item.q26_reason_no_benefit,
            comment_other: item.comment_other,
            
            total_score: item.total_score
          });
        }
      });

      const errorCount = errors.length;
      let successCount = 0;

      // ส่งข้อมูลทีละรายการ
      for (const item of validData) {
        try {
          if (activeTab === 1) {
            await addAssessmentReportCoopMulti(item);
          } else {
            await addAssessmentCompanyCoopMulti(item);
          }
          successCount++;
        } catch (error) {
          console.error('Error submitting data:', error);
          errors.push(`เกิดข้อผิดพลาดในการบันทึกข้อมูล ${item.student_name || item.req_id}`);
        }
      }

      // แสดงผลลัพธ์
      if (successCount > 0) {
        dispatch(updateSnackbar({
          open: true,
          severity: "success",
          content: `บันทึกสำเร็จ ${successCount} รายการ${errorCount > 0 ? `, ล้มเหลว ${errorCount} รายการ` : ''}`
        }));
        
        if (onRefresh) {
          onRefresh();
        }
        
        if (errorCount === 0) {
          handleClose();
        }
      } else {
        dispatch(updateSnackbar({
          open: true,
          severity: "error",
          content: `ไม่สามารถบันทึกข้อมูลได้: ${errors.join(', ')}`
        }));
      }

    } catch (error) {
      console.error('Bulk input error:', error);
      dispatch(updateSnackbar({
        open: true,
        severity: "error",
        content: "เกิดข้อผิดพลาดในการนำเข้าข้อมูล"
      }));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setBulkInputData('');
    setPreviewData([]);
    setActiveTab(0);
    onClose();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setBulkInputData(''); // Clear input when switching tabs
    setPreviewData([]);
  };

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 1200,
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    overflow: 'auto',
    borderRadius: 2,
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        <Typography variant="h6" component="h2" gutterBottom>
          📊 เพิ่มคะแนนแบบกลุ่ม (สหกิจศึกษา)
        </Typography>

        {/* Tab Navigation */}
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="🧑‍💼 คะแนนพี่เลี้ยง" />
          <Tab label="📖 คะแนนเล่มรายงาน" />
        </Tabs>

        {/* Instructions for Supervisor Tab */}
        {activeTab === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              วิธีใช้ (คะแนนพี่เลี้ยง):
            </Typography>
            <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>เลือกข้อมูลจาก Google Forms Response Sheet ตั้งแต่คอลัมน์ <strong>ประทับเวลา</strong> ถึง <strong>ข้อคิดเห็นอื่นๆ</strong></li>
              <li>Copy (Ctrl+C / Cmd+C) แล้ว Paste ลงในช่องด้านล่าง</li>
              <li>ระบบจะค้นหาและจับคู่ชื่อนิสิต (คอลัมน์ที่ 6) กับ Request ID อัตโนมัติ</li>
              <li>ตรวจสอบข้อมูลใน Preview ก่อนกดปุ่ม "นำเข้าข้อมูล"</li>
            </ol>
            <Typography variant="caption" color="text.secondary">
              📋 <strong>โครงสร้างข้อมูล:</strong><br/>
              • คอลัมน์ 1-8: ข้อมูลพื้นฐาน (ประทับเวลา, Email, ผู้ประเมิน, ชื่อนิสิต, หลักสูตร, สถานประกอบการ)<br/>
              • คอลัมน์ 9-28: คะแนนการประเมิน 20 หัวข้อ (คะแนนเต็ม 10 x 20 = 200 คะแนน)<br/>
              • คอลัมน์ 29+: ความคิดเห็นและข้อมูลเพิ่มเติม
            </Typography>
          </Alert>
        )}

        {/* Instructions for Report Tab */}
        {activeTab === 1 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              วิธีใช้ (คะแนนเล่มรายงาน):
            </Typography>
            <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>เลือกข้อมูลจาก Google Forms Response Sheet ตั้งแต่คอลัมน์ <strong>ประทับเวลา</strong> ถึง <strong>ข้อคิดเห็น</strong></li>
              <li>Copy (Ctrl+C / Cmd+C) แล้ว Paste ลงในช่องด้านล่าง</li>
              <li>ระบบจะค้นหาและจับคู่ชื่อนิสิต (คอลัมน์ที่ 6) กับ Request ID อัตโนมัติ</li>
              <li>ตรวจสอบข้อมูลใน Preview ก่อนกดปุ่ม "นำเข้าข้อมูล"</li>
            </ol>
            <Typography variant="caption" color="text.secondary">
              📋 <strong>โครงสร้างข้อมูล (24 คอลัมน์):</strong><br/>
              • คอลัมน์ 1-9: ข้อมูลพื้นฐาน (ประทับเวลา, Email, ผู้ประเมิน, ตำแหน่ง, เบอร์โทร, <strong>ชื่อนิสิต</strong>, หลักสูตร, สถานประกอบการ, ชื่อโครงงาน)<br/>
              • คอลัมน์ 10-23: คะแนนการประเมิน 14 หัวข้อ<br/>
              &nbsp;&nbsp;- กิตติกรรมประกาศ (5), บทคัดย่อ (5), สารบัญ (5), บทที่ 2 วัตถุประสงค์ (5)<br/>
              &nbsp;&nbsp;- บทที่ 3 วิธีดำเนินงาน (5), บทที่ 4 ผลการดำเนินงาน (20), การวิเคราะห์ผล (10)<br/>
              &nbsp;&nbsp;- บทที่ 5 สรุป (10), บทที่ 5 ข้อเสนอแนะ (5), การเขียน (10)<br/>
              &nbsp;&nbsp;- การสะกดคำ (5), รูปเล่ม (5), เอกสารอ้างอิง (5), ภาคผนวก (5)<br/>
              • คอลัมน์ 24: ข้อคิดเห็น<br/>
              • <strong>รวม: 100 คะแนน</strong>
            </Typography>
          </Alert>
        )}

        <FormControl fullWidth sx={{ mb: 2 }}>
          <TextField
            multiline
            rows={8}
            placeholder={
              activeTab === 0 
                ? "วางข้อมูลคะแนนพี่เลี้ยงจาก Google Sheets ที่นี่..." 
                : "วางข้อมูลคะแนนเล่มรายงานจาก Google Sheets ที่นี่..."
            }
            value={bulkInputData}
            onChange={(e) => setBulkInputData(e.target.value)}
            disabled={isProcessing}
          />
          <FormHelperText>
            {previewData.length > 0 
              ? `ตรวจพบข้อมูล ${previewData.length} รายการ` 
              : 'รอข้อมูล...'}
          </FormHelperText>
        </FormControl>

        {previewData.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              ตัวอย่างข้อมูล:
            </Typography>
            <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>บรรทัด</TableCell>
                    <TableCell>ชื่อนิสิต</TableCell>
                    <TableCell>Request ID</TableCell>
                    <TableCell>คะแนนรวม</TableCell>
                    <TableCell>สถานะ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.slice(0, 10).map((item) => (
                    <TableRow key={item.temp_id}>
                      <TableCell>{item.lineNumber}</TableCell>
                      <TableCell>{item.student_name}</TableCell>
                      <TableCell>
                        {item.found_req_id ? (
                          <Chip label={item.found_req_id} size="small" color="success" />
                        ) : (
                          <Chip label="ไม่พบ" size="small" color="error" />
                        )}
                      </TableCell>
                      <TableCell>
                        {item.total_score}/{activeTab === 0 ? '200' : '100'}
                      </TableCell>
                      <TableCell>
                        {item.found_req_id ? (
                          <Chip label="พร้อม" size="small" color="success" />
                        ) : (
                          <Chip label="ไม่พบข้อมูล" size="small" color="error" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {previewData.length > 10 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                แสดง 10 รายการแรก จากทั้งหมด {previewData.length} รายการ
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={handleClose} disabled={isProcessing}>
            ยกเลิก
          </Button>
          <Button
            variant="outlined"
            onClick={handleShowJson}
            disabled={previewData.length === 0 || isProcessing}
          >
            📋 ดู JSON
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkSubmit}
            disabled={previewData.length === 0 || isProcessing}
          >
            {isProcessing ? 'กำลังนำเข้า...' : `นำเข้าข้อมูล (${previewData.length})`}
          </Button>
        </Box>

        {/* JSON Preview Modal */}
        <Modal open={showJsonModal} onClose={() => setShowJsonModal(false)}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80%',
            maxWidth: 900,
            maxHeight: '80vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            overflow: 'auto',
          }}>
            <Typography variant="h6" component="h2" gutterBottom>
              🔍 ข้อมูล JSON ที่จะส่งไป API
            </Typography>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              ข้อมูลนี้จะถูกส่งไปยัง API {activeTab === 0 
                ? <code>/add-assessment-companycoopmulti</code> 
                : <code>/add-assessment-reportcoopmulti</code>} สำหรับแต่ละนิสิต
            </Alert>

            <Box sx={{ 
              bgcolor: '#f5f5f5', 
              p: 2, 
              borderRadius: 1, 
              maxHeight: '50vh', 
              overflow: 'auto',
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all'
            }}>
              {jsonPreview}
            </Box>

            <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button 
                variant="outlined" 
                onClick={() => {
                  navigator.clipboard.writeText(jsonPreview);
                  dispatch(updateSnackbar({
                    open: true,
                    severity: "success",
                    content: "คัดลอก JSON เรียบร้อย"
                  }));
                }}
              >
                📋 คัดลอก JSON
              </Button>
              <Button variant="contained" onClick={() => setShowJsonModal(false)}>
                ปิด
              </Button>
            </Box>
          </Box>
        </Modal>
      </Box>
    </Modal>
  );
};

export default BulkInputModalCoop;
